import React, {Suspense, useEffect, useState} from 'react';
import {IStatusModel} from './model/appModel';
import config from './service/config';
import './static/css/index-header.less';
import HeaderPlaceholder from './component/header/HeaderPlaceholder';
import Header from './component/header/Header';
import {PageWrapper, ContentArea} from './component/util/StyledElements';
import ScreenReaderHelper from './component/util/ScreenReaderHelper';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Analytics from './service/analytics';
import Status from './component/status/Status';
import Constants, {StatusPages} from './constants/Constants';
import logging from './service/logging';
import {renderToStaticMarkup} from 'react-dom/server';
import Stoplight from './static/svg/stoplight.svg';
import Bullseye from './static/png/bullseye.png';
import Umbrella from './static/png/umbrella.png';
import CrackedGlasses from './static/svg/cracked-glasses.svg';
import MediaPrivate from './static/svg/MediaPrivateStatusGlyph.svg';
import Box from './static/png/box.png';
import Refresh from './static/png/refresh.png';
import Satellite from './static/png/satellite.png';
import Clapboard from './static/svg/clapboard.svg';
import RememberMe from './static/svg/remember-me.svg';
import Lock from './static/png/lock.png';
import {BrowserRouter} from 'react-router-dom';
import {QueryParamProvider} from 'use-query-params';
import StateProvider from './context/stateProvider';
import {Spinner} from '@techsmith/tsc-cloud-style-guide';
import AccessPhraseInput from './component/status/AccessPhraseInput';
import {ReactRouter5Adapter} from 'use-query-params/adapters/react-router-5';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {createRoot} from 'react-dom/client';

const StatusContainer = styled.div`
   display: flex;
   flex: 1 1 auto;
   max-width: 100%;
`;

const SpinnerContainer = styled.div`
   margin-top: 2rem;
`;

const navigateToSignOutPageWithRedirectToSignInPage = () => {
   window.location.href = config.statusConfig.signOutRedirectToSignInUrl;
};

const clapboardSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<Clapboard />))}`;
const crackedGlassesSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<CrackedGlasses />))}`;
const rememberMeSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<RememberMe />))}`;
const stoplightSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<Stoplight />))}`;
const mediaPrivateSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<MediaPrivate />))}`;

export const StatusRoot: React.FC = () => {
   const {t} = useTranslation();

   const [signInButtonClicked, setSignInButtonClicked] = useState(false);

   const canCloseWindow = !!window.opener || window.history.length === 1;
   const isEmbedded = window.top !== window.self;

   useEffect(() => {
      void Analytics.initialize();
   }, []);

   const closeWindow = () => {
      close();
   };

   const navigateToSignInPage = () => {
      // If the page is embedded, we open a sign in page in a new tab and allow the user to refresh the page after signing in
      if (isEmbedded) {
         if (signInButtonClicked) {
            // "Refresh" the page by navigating away from the status page to the original destination
            window.location.href = config.statusConfig.returnUrl;
         } else {
            // Open the sign in page in a new tab and convert the Sign In button to a Refresh Page button
            window.open(config.statusConfig.signInNewTabUrl, '_blank');
            setSignInButtonClicked(true);
         }
      } else {
         window.location.href = config.environmentData.SignInUrl;
      }
   };

   let pageTitle: string;
   let statusContent: React.JSX.Element;
   switch (config.statusConfig.statusPage) {
      case StatusPages.accessDenied: {
         pageTitle = t('status.accessDenied.pageTitle');
         statusContent = (
            <Status
               imageAlt={t('status.accessDenied.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={t('status.accessDenied.text')}
               title={t('status.accessDenied.title')}
            />
         );
         break;
      }
      case StatusPages.accessPhraseRequired: {
         pageTitle = t('status.accessPhraseRequired.title');
         const text = <AccessPhraseInput />;
         statusContent = (
            <Status
               imageAlt={t('status.accessPhraseRequired.imageAltText')}
               imageSrc={mediaPrivateSvgDataUri}
               text={text}
               title={t('status.accessPhraseRequired.title')}
            />
         );
         break;
      }
      case StatusPages.classicRecorderLoginSuccess: {
         pageTitle = t('status.classicRecorderLoginSuccess.pageTitle');
         const text = (
            <>
               {t('status.classicRecorderLoginSuccess.text')}
               <br />
               <a href={Constants.navigation.myLibraryLink}>
                  {t('status.classicRecorderLoginSuccess.linkText')}
               </a>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.classicRecorderLoginSuccess.imageAltText')}
               imageSrc={clapboardSvgDataUri}
               text={text}
               title={t('status.classicRecorderLoginSuccess.title')}
            />
         );
         break;
      }
      case StatusPages.classicRecorderLogoutSuccess: {
         pageTitle = t('status.classicRecorderLogoutSuccess.pageTitle');
         statusContent = (
            <Status
               imageAlt={t('status.classicRecorderLogoutSuccess.imageAltText')}
               imageSrc={rememberMeSvgDataUri}
               text={t('status.classicRecorderLogoutSuccess.text')}
               title={t('status.classicRecorderLogoutSuccess.title')}
            />
         );
         break;
      }
      case StatusPages.classicRecordersUnavailable: {
         pageTitle = t('status.classicRecordersUnavailable.pageTitle');
         const text = (
            <>
               {t('status.classicRecordersUnavailable.text')}
               {' '}
               <a href="/">
                  {config.applicationFullName}
               </a>
               .
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.classicRecordersUnavailable.imageAltText')}
               imageSrc={Umbrella}
               text={text}
               title={t('status.classicRecordersUnavailable.title')}
            />
         );
         break;
      }
      case StatusPages.identityProviderMismatch: {
         pageTitle = t('status.identityProviderMismatch.pageTitle');
         const text = (
            <>
               {t('status.identityProviderMismatch.text1')}
               <br />
               {t('status.identityProviderMismatch.text2')}
            </>
         );
         statusContent = (
            <Status
               buttonText={t('status.identityProviderMismatch.buttonText')}
               imageAlt={t('status.identityProviderMismatch.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               onButtonClick={navigateToSignOutPageWithRedirectToSignInPage}
               text={text}
               title={t('status.identityProviderMismatch.title')}
            />
         );
         break;
      }
      case StatusPages.internalServerError: {
         pageTitle = t('status.internalServerError.pageTitle');
         const text = (
            <>
               <p>
                  {t('status.internalServerError.text1')}
                  <br />
                  {t('status.internalServerError.text2')}
               </p>
               <p>
                  {t('status.internalServerError.text3')}
                  {' '}
                  <a
                     href={Constants.navigation.contactSupportLink}
                     target="_blank"
                     rel="noopener noreferrer nofollow"
                  >
                     {t('status.internalServerError.linkText')}
                  </a>
                  .
               </p>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.internalServerError.imageAltText')}
               imageSrc={Umbrella}
               text={text}
               title={t('status.internalServerError.title')}
            />
         );
         break;
      }
      case StatusPages.ltiBlockedCookiesAddContent: {
         pageTitle = t('status.ltiBlockedCookiesAddContent.pageTitle');
         const text = (
            <>
               {t('status.ltiBlockedCookiesAddContent.text')}
               {' '}
               <a
                  target="_blank"
                  href={Constants.navigation.browserDoesNotAllowCookiesSupportLink}
                  rel="noopener noreferrer nofollow"
               >
                  {t('status.ltiBlockedCookiesAddContent.linkText')}
               </a>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.ltiBlockedCookiesAddContent.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={text}
               title={t('status.ltiBlockedCookiesAddContent.title', {applicationShortName: config.applicationShortName})}
            />
         );
         break;
      }
      case StatusPages.ltiNotAuthorized: {
         pageTitle = t('status.ltiNotAuthorized.pageTitle');
         const text = (
            <>
               {t('status.ltiNotAuthorized.text1', {applicationFullName: config.applicationFullName})}
               <br />
               {t('status.ltiNotAuthorized.text2')}
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.ltiNotAuthorized.imageAltText')}
               imageSrc={Lock}
               text={text}
               title={t('status.ltiNotAuthorized.title')}
            />
         );
         break;
      }
      case StatusPages.ltiPrivate: {
         pageTitle = t('status.ltiPrivate.pageTitle');
         statusContent = (
            <Status
               imageAlt={t('status.ltiPrivate.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={t('status.ltiPrivate.text')}
               title={t('status.ltiPrivate.title')}
            />
         );
         break;
      }
      case StatusPages.ltiSignInFailed: {
         pageTitle = t('status.ltiSignInFailed.pageTitle');
         const text = (
            <>
               {t('status.ltiSignInFailed.text1', {applicationFullName: config.applicationFullName})}
               <br />
               {t('status.ltiSignInFailed.text2')}
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.ltiSignInFailed.imageAltText')}
               imageSrc={Satellite}
               text={text}
               title={t('status.ltiSignInFailed.title')}
            />
         );
         break;
      }
      case StatusPages.ltiSwitchedUser: {
         pageTitle = t('status.ltiSwitchedUser.pageTitle');
         if (isEmbedded) {
            const text = (
               <>
                  <p>
                     {t('status.ltiSwitchedUser.embedText1')}
                  </p>
                  <p>
                     {t('status.ltiSwitchedUser.embedText2')}
                  </p>
               </>
            );
            statusContent = (
               <Status
                  imageAlt={t('status.ltiSwitchedUser.embedImageAltText')}
                  imageSrc={Refresh}
                  text={text}
                  title={t('status.ltiSwitchedUser.embedTitle')}
               />
            );
         } else {
            const text = (
               <>
                  <p>
                     {t('status.ltiSwitchedUser.text1')}
                  </p>
                  <p>
                     {t('status.ltiSwitchedUser.text2')}
                  </p>
               </>
            );
            statusContent = (
               <Status
                  buttonText={canCloseWindow ? t('status.ltiSwitchedUser.buttonText') : null}
                  imageAlt={t('status.ltiSwitchedUser.imageAltText')}
                  imageSrc={Umbrella}
                  onButtonClick={canCloseWindow ? closeWindow : null}
                  text={text}
                  title={t('status.ltiSwitchedUser.title')}
               />
            );
         }
         break;
      }
      case StatusPages.noContentSelected: {
         pageTitle = t('status.noContentSelected.pageTitle');
         const text = (
            <>
               {t('status.noContentSelected.text1')}
               <br />
               {t('status.noContentSelected.text2')}
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.noContentSelected.imageAltText')}
               imageSrc={Box}
               text={text}
               title={t('status.noContentSelected.title')}
            />
         );
         break;
      }
      case StatusPages.notFound: {
         pageTitle = t('status.notFound.pageTitle');
         const text = (
            <p>
               {t('status.notFound.text1')}
               <br />
               {t('status.notFound.text2')}
               {' '}
               <a href="/">
                  <strong>
                     {config.applicationFullName}
                  </strong>
               </a>
               <br />
               {!config.featureSwitches.DisableSearch && t('status.notFound.text3')}
            </p>
         );
         statusContent = (
            <Status
               imageAlt={t('status.notFound.imageAltText')}
               imageSrc={Bullseye}
               text={text}
               title={t('status.notFound.title')}
            />
         );
         break;
      }
      case StatusPages.notSignedIn: {
         if (config.user.IsSignedIn) {
            // For Knowmia Enterprise users who use LTI integration with the FallbackToSsoForLti feature switch, they will reach this page as their "authentication endpoint"
            // As a result of that, we must immediately redirect them to their destination if they are logged in
            window.location.href = config.statusConfig.returnUrl;
            return (
               <SpinnerContainer>
                  <Spinner />
               </SpinnerContainer>
            );
         }

         pageTitle = t('status.notSignedIn.pageTitle');
         const text = isEmbedded ? (
            <>
               {t('status.notSignedIn.text')}
               {' '}
               <a
                  target="_blank"
                  href={config.statusConfig.signInNewTabUrl}
                  rel="noopener noreferrer nofollow"
               >
                  {t('status.notSignedIn.linkText')}
               </a>
            </>
         ) : null;
         statusContent = (
            <Status
               buttonText={signInButtonClicked ? t('status.refreshPageButtonText') : t('status.signInButtonText')}
               imageAlt={t('status.notSignedIn.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               onButtonClick={navigateToSignInPage}
               text={text}
               title={t('status.notSignedIn.title')}
            />
         );
         break;
      }
      case StatusPages.readOnlyMode: {
         pageTitle = t('status.readOnlyMode.pageTitle');
         statusContent = (
            <Status
               imageAlt={t('status.readOnlyMode.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={t('status.readOnlyMode.text')}
               title={t('status.readOnlyMode.title')}
            />
         );
         break;
      }
      case StatusPages.searchUnavailable: {
         pageTitle = t('status.searchUnavailable.pageTitle');
         const text = (
            <>
               {t('status.searchUnavailable.text')}
               {' '}
               <a href="/">
                  {config.applicationFullName}
               </a>
               .
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.searchUnavailable.imageAltText')}
               imageSrc={crackedGlassesSvgDataUri}
               text={text}
               title={t('status.searchUnavailable.title')}
            />
         );
         break;
      }
      case StatusPages.thirdPartyTranscriptionPendingApproval: {
         pageTitle = t('status.thirdPartyTranscriptionPendingApproval.pageTitle');
         const text = (
            <>
               {t('status.thirdPartyTranscriptionPendingApproval.text')}
               <br />
               <a href={config.statusConfig.returnUrl}>
                  {t('status.thirdPartyTranscriptionPendingApproval.linkText')}
               </a>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.thirdPartyTranscriptionPendingApproval.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={text}
               title={t('status.thirdPartyTranscriptionPendingApproval.title')}
            />
         );
         break;
      }
      case StatusPages.transcriptionInProgress: {
         pageTitle = t('status.transcriptionInProgress.pageTitle');
         const text = (
            <>
               {t('status.transcriptionInProgress.text')}
               <br />
               <a href={config.statusConfig.returnUrl}>
                  {t('status.transcriptionInProgress.linkText')}
               </a>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.transcriptionInProgress.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={text}
               title={t('status.transcriptionInProgress.title')}
            />
         );
         break;
      }
      case StatusPages.wrongEmailDomain: {
         pageTitle = t('status.wrongEmailDomain.pageTitle');
         const text = (
            <>
               {t('status.wrongEmailDomain.text1')}
               <br />
               {t('status.wrongEmailDomain.text2')}
               {' '}
               {config.statusConfig.allowedEmailDomains}
               <p>
                  <a href={config.environmentData.SignOutUrl}>
                     {t('status.wrongEmailDomain.linkText')}
                  </a>
                  {' '}
                  {t('status.wrongEmailDomain.text3')}
                  <br />
                  {t('status.wrongEmailDomain.text4')}
               </p>
            </>
         );
         statusContent = (
            <Status
               imageAlt={t('status.wrongEmailDomain.imageAltText')}
               imageSrc={stoplightSvgDataUri}
               text={text}
               title={t('status.wrongEmailDomain.title')}
            />
         );
         break;
      }
      case StatusPages.mediaUnavailable: {
         pageTitle = t('status.mediaUnavailable.pageTitle');
         statusContent = (
            <Status
               imageAlt={t('status.readOnlyMode.imageAltText')}
               text={t('status.mediaUnavailable.text')}
               title={t('status.mediaUnavailable.title')}
            />
         );
         break;
      }
      default: {
         // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
         logging.error(`Unknown status page ${config.statusConfig.statusPage}`);
         break;
      }
   }

   document.title = `${pageTitle} - ${config.applicationFullName}`;

   return (
      <DndProvider backend={HTML5Backend}>
         <PageWrapper>
            <ScreenReaderHelper
               href="#start-of-content"
               text={t('accessibility.skipLinkText')}
            />
            {!isEmbedded && (
               <Suspense fallback={<HeaderPlaceholder />}>
                  <Header
                     hideSearch={config.featureSwitches.DisableSearch}
                     hideEntitlementInfo={false}
                     hideThemePicker={false}
                     hideNavLinkTabs={false}
                  />
               </Suspense>
            )}
            <ContentArea>
               <StatusContainer
                  id="start-of-content"
                  data-test-id="status-wrapper"
               >
                  {statusContent}
               </StatusContainer>
            </ContentArea>
         </PageWrapper>
      </DndProvider>
   );
};

export default (model: IStatusModel) => {
   const statusRoot = document.getElementById('status-root');
   if (!statusRoot) {
      return;
   }

   document.body.classList.add('app-body');

   config.initStatus(model);

   createRoot(statusRoot).render(
      <BrowserRouter>
         <QueryParamProvider adapter={ReactRouter5Adapter}>
            <StateProvider>
               <StatusRoot />
            </StateProvider>
         </QueryParamProvider>
      </BrowserRouter>
   );
};
